// Ghost text（Copilot 風インライン補完）
//
// 役割:
//   textarea のキャレット直後に、薄グレーの提案テキストを inline で表示する。
//   Tab で採用、Esc / 他キーで破棄。
//
// 仕組み:
//   Phase 1 の overlay と同じく、textarea の wrapper に absolute で <div> を重ねる。
//   ただし Phase 2 の ghost 専用 overlay は「透明本文 + ghost span」だけを描画する。
//   キャレット位置は caret.js で取得して、そこから表示を開始する。
//
// 仕様:
//   - 提案テキストは options.fetcher で取得（注入式）。未指定なら固定文字列で動作（PoC モード）
//   - 表示トリガは input 停止後 1000ms
//   - IME 変換中は表示しない。変換確定後 150ms debounce で再判定
//   - Tab / Esc 以外のキー入力で破棄
//   - カーソル右に文字がある（行末・文末でない）時は発動しない

import { getCaretCoordinates } from '../caret'

// 入力停止後すぐ LLM を呼びに行く（typing burst だけ debounce で吸収）
// 200ms あれば連続タイピング中は再発火が抑制され、停止後はほぼ即時にリクエスト発射
const TRIGGER_DELAY_MS = 200
const IME_RESUME_DELAY_MS = 150
const POC_SUGGESTION = '（続きの提案テキストです）'

const state = new WeakMap()

function createGhostOverlay(wrapper) {
  const overlay = document.createElement('div')
  overlay.className = 'proofread-ghost-overlay'
  overlay.setAttribute('aria-hidden', 'true')
  const cs = window.getComputedStyle(wrapper)
  if (cs.position === 'static') {
    wrapper.style.position = 'relative'
  }
  wrapper.appendChild(overlay)
  return overlay
}

function syncGhostOverlayStyle(overlay, textarea) {
  const cs = window.getComputedStyle(textarea)
  // フォント関係は ghost span 側で合わせるため、overlay 自体は枠合わせだけで OK
  overlay.style.position = 'absolute'
  overlay.style.top = `${textarea.offsetTop}px`
  overlay.style.left = `${textarea.offsetLeft}px`
  overlay.style.width = `${textarea.offsetWidth}px`
  overlay.style.height = `${textarea.offsetHeight}px`
  overlay.style.pointerEvents = 'none'
  overlay.style.overflow = 'hidden'
  overlay.style.background = 'transparent'
  overlay.style.zIndex = '3' // Phase 1 overlay (z-index 2) より上
  // ghost span のフォント参照用にコピー
  overlay.style.font = cs.font
  overlay.style.lineHeight = cs.lineHeight
  overlay.style.letterSpacing = cs.letterSpacing
}

function destroyGhostOverlay(overlay) {
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay)
  }
}

/**
 * フォールバック proposer（fetcher 未指定時の動作確認用）。
 * 本番では options.fetcher を必ず注入する。
 */
function defaultFetcher() {
  return Promise.resolve(POC_SUGGESTION)
}

/**
 * textarea に ghost text を attach する。
 *
 * @param {HTMLTextAreaElement} textarea
 * @param {object} [options]
 * @param {(args: { textBeforeCursor: string, textAfterCursor: string, context: string }) => Promise<string>} [options.fetcher]
 *   候補テキストを返す async 関数。空文字を返した場合は ghost を出さない。
 * @param {string} [options.context]  対象欄ラベル（例: "志望動機"）
 * @returns {{ destroy: () => void }}  detach 関数
 */
export function attachGhostText(textarea, options) {
  if (!textarea || state.has(textarea)) {
    return { destroy: () => {} }
  }
  const wrapper = textarea.parentNode
  if (!wrapper) return { destroy: () => {} }

  const fetcher =
    options && typeof options.fetcher === 'function'
      ? options.fetcher
      : defaultFetcher
  const context = (options && options.context) || '文章'
  // 採用 / 破棄イベント通知（注入式・任意）。action と任意 extra を渡す
  const onEvent =
    options && typeof options.onEvent === 'function' ? options.onEvent : null

  const overlay = createGhostOverlay(wrapper)
  syncGhostOverlayStyle(overlay, textarea)

  const ctx = {
    overlay,
    ghostSpan: null,
    ghostText: '',
    ghostVisible: false,
    caretAtShow: -1, // ghost 表示時のキャレット位置（カーソルが動いたら消すため）
    composing: false,
    triggerTimer: null,
    resumeTimer: null,
    inflightController: null, // 進行中の LLM リクエストの AbortController
    // IME 変換中に到着した suggestion を保留する
    pendingSuggestion: null,
    pendingForCaret: -1,
    pendingForText: '',
    handlers: {},
    resizeObserver: null
  }

  function clearTriggerTimer() {
    if (ctx.triggerTimer) {
      clearTimeout(ctx.triggerTimer)
      ctx.triggerTimer = null
    }
  }
  function clearResumeTimer() {
    if (ctx.resumeTimer) {
      clearTimeout(ctx.resumeTimer)
      ctx.resumeTimer = null
    }
  }

  // 進行中の LLM リクエストを破棄
  function abortInflight() {
    if (ctx.inflightController) {
      try {
        ctx.inflightController.abort()
      } catch (e) {
        /* noop */
      }
      ctx.inflightController = null
    }
    // pending も無効化（pending は inflight と同じく「古くなったら捨てる」性質）
    ctx.pendingSuggestion = null
    ctx.pendingForCaret = -1
    ctx.pendingForText = ''
  }

  // ghost の DOM 要素のみ消す（inflight・pending は触らない）
  // IME 開始時など「visual だけ隠したいが request は活かしたい」シーン用
  function hideGhostVisualOnly() {
    if (ctx.ghostSpan && ctx.ghostSpan.parentNode) {
      ctx.ghostSpan.parentNode.removeChild(ctx.ghostSpan)
    }
    ctx.ghostSpan = null
    ctx.ghostChip = null
    ctx.ghostText = ''
    ctx.ghostVisible = false
    ctx.caretAtShow = -1
    overlay.innerHTML = ''
  }

  function hideGhost() {
    clearTriggerTimer()
    abortInflight()
    hideGhostVisualOnly()
  }

  function renderGhostAtCaret(suggestion) {
    if (!suggestion) return
    // キャレットが見えない（textarea 非表示）ときは出さない
    if (textarea.offsetWidth === 0 || textarea.offsetHeight === 0) return

    syncGhostOverlayStyle(overlay, textarea)
    overlay.innerHTML = ''

    const caretPos = textarea.selectionEnd
    const coords = getCaretCoordinates(textarea, caretPos)

    // スクロール補正: textarea が縦スクロールしている場合は引く
    const top = coords.top - textarea.scrollTop
    const left = coords.left - textarea.scrollLeft

    // overlay の内側 padding は textarea と別にせず、absolute 座標で配置する
    const span = document.createElement('span')
    span.className = 'proofread-ghost'
    span.textContent = suggestion
    span.style.position = 'absolute'
    // textarea の padding 分だけオフセット
    const cs = window.getComputedStyle(textarea)
    const padTop = parseFloat(cs.paddingTop) || 0
    const padLeft = parseFloat(cs.paddingLeft) || 0
    const borderTop = parseFloat(cs.borderTopWidth) || 0
    const borderLeft = parseFloat(cs.borderLeftWidth) || 0
    // ghost span は textarea と同じ font / line-height を使うことで、
    // テキスト行の中央に揃える。微調整で 2px 上にオフセット。
    span.style.font = cs.font
    span.style.lineHeight = cs.lineHeight
    span.style.letterSpacing = cs.letterSpacing
    span.style.top = `${top + padTop + borderTop - 2}px`
    span.style.left = `${left + padLeft + borderLeft}px`
    span.style.whiteSpace = 'pre-wrap'
    // スマホは Tab キーが使えないため、ghost span 自体のタップで採用できるようにする。
    // pointer-events: auto + mousedown.preventDefault で textarea の focus を保ちつつ
    // click で acceptGhost を呼ぶ（波線タップと同じパターン）。
    span.style.pointerEvents = 'auto'
    span.style.cursor = 'pointer'
    span.addEventListener('mousedown', (e) => {
      e.preventDefault()
    })
    span.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      acceptGhost()
    })

    overlay.appendChild(span)
    ctx.ghostSpan = span
    ctx.ghostText = suggestion
    ctx.ghostVisible = true
    ctx.caretAtShow = caretPos

    // 採用チップ：右下に浮かべるフロート。「タップで採用できる」ことを
    // 発見しやすくするためで、ghost 表示中のみ可視。
    const chip = document.createElement('button')
    chip.type = 'button'
    chip.className = 'proofread-ghost-accept-chip'
    chip.textContent = 'タップで採用'
    chip.style.position = 'absolute'
    chip.style.right = '8px'
    chip.style.bottom = '8px'
    chip.style.pointerEvents = 'auto'
    chip.addEventListener('mousedown', (e) => {
      e.preventDefault()
    })
    chip.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      acceptGhost()
    })
    overlay.appendChild(chip)
    ctx.ghostChip = chip
  }

  // カーソル右に文字がある（行末でも文末でもない）時は ghost を出さない。
  // 既存テキストと重なるのを避け、「続きを書く」場面だけで発動する。
  function canShowGhost() {
    const text = textarea.value || ''
    const caretPos = textarea.selectionEnd
    const next = text[caretPos]
    return next === undefined || next === '\n'
  }

  function requestGhost() {
    clearTriggerTimer()
    abortInflight() // 直前の in-flight があれば破棄
    if (ctx.composing) return
    // 既に表示中なら一旦消してから
    if (ctx.ghostVisible) hideGhost()
    // 行末・文末でない場合は trigger しない
    if (!canShowGhost()) return
    ctx.triggerTimer = setTimeout(async () => {
      if (ctx.composing) return
      // focus していないときは出さない
      if (document.activeElement !== textarea) return
      // setTimeout 待機中にカーソルが移動している可能性があるので再チェック
      if (!canShowGhost()) return
      const caretPos = textarea.selectionEnd
      const text = textarea.value || ''
      const textBeforeCursor = text.slice(0, caretPos)
      const textAfterCursor = text.slice(caretPos)

      // AbortController を準備（ブラウザ側で未対応なら null）
      const controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null
      ctx.inflightController = controller
      const signal = controller ? controller.signal : null

      try {
        const suggestion = await fetcher({
          textBeforeCursor,
          textAfterCursor,
          context,
          signal
        })

        if (ctx.inflightController !== controller) return
        ctx.inflightController = null
        if (!state.has(textarea)) return
        if (!suggestion || !suggestion.trim()) return

        // IME 変換中: 描画せず pending として保持。compositionend で再評価する
        if (ctx.composing) {
          ctx.pendingSuggestion = suggestion
          ctx.pendingForCaret = caretPos
          ctx.pendingForText = textBeforeCursor
          return
        }

        if (document.activeElement !== textarea) return
        if (!canShowGhost()) return

        // キャレット位置の緩めチェック:
        //   - 完全一致 (request 時から動いてない) → そのまま OK
        //   - 末尾追記 (request 中にユーザーが少し文字追加) → prefix 一致 + 追記 N 文字以内なら継続表示
        //   - それ以外 (削除・中間編集・追記が大きい) → drop
        const STALE_APPEND_THRESHOLD = 5 // 5文字以上追記されたら古い提案と見なし破棄
        const currentText = textarea.value || ''
        const currentCaret = textarea.selectionEnd
        if (currentCaret !== caretPos) {
          const currentBefore = currentText.slice(0, currentCaret)
          if (!currentBefore.startsWith(textBeforeCursor)) return
          const appendedLen = currentBefore.length - textBeforeCursor.length
          if (appendedLen > STALE_APPEND_THRESHOLD) {
            return // 追記が多すぎる → 古い提案と見なし破棄
          }
        }

        renderGhostAtCaret(suggestion)
      } catch (err) {
        // 自分自身が abort した場合は何もしない
        if (signal && signal.aborted) return
        // ネットワーク・LLM エラーは ghost を出さないだけで、ユーザーには通知しない
        // eslint-disable-next-line no-console
        console.warn('[ghost-text] fetch failed', err)
      }
    }, TRIGGER_DELAY_MS)
  }

  function acceptGhost() {
    if (!ctx.ghostVisible || !ctx.ghostText) return
    const suggestion = ctx.ghostText
    const caret = ctx.caretAtShow >= 0 ? ctx.caretAtShow : textarea.selectionEnd
    const text = textarea.value || ''
    const next = text.slice(0, caret) + suggestion + text.slice(caret)
    // v-model 同期のため input イベントを発火させる
    const proto = Object.getPrototypeOf(textarea)
    const descriptor = Object.getOwnPropertyDescriptor(proto, 'value')
    if (descriptor && descriptor.set) {
      descriptor.set.call(textarea, next)
    } else {
      textarea.value = next
    }
    const newCaret = caret + suggestion.length
    textarea.selectionStart = newCaret
    textarea.selectionEnd = newCaret
    // Vue の v-model 同期
    textarea.dispatchEvent(new Event('input', { bubbles: true }))
    // 採用イベント記録（Tab / タップ / chip いずれの経路もここを通る）
    if (onEvent) onEvent('accept', { payload: { length: suggestion.length } })
    hideGhost()
  }

  // ===== イベントハンドラ =====

  const onCompositionStart = () => {
    ctx.composing = true
    clearResumeTimer()
    // visual だけ消す。inflight リクエストは継続させて、
    // 変換中に response が届いたら pending として保持する
    hideGhostVisualOnly()
  }
  const onCompositionEnd = () => {
    ctx.composing = false
    clearResumeTimer()
    ctx.resumeTimer = setTimeout(() => {
      if (ctx.composing) return

      // pending suggestion がある場合は、テキストが整合していれば再利用する
      if (ctx.pendingSuggestion) {
        const currentBefore = (textarea.value || '').slice(
          0,
          textarea.selectionEnd
        )
        const sameContext =
          textarea.selectionEnd === ctx.pendingForCaret &&
          currentBefore === ctx.pendingForText
        if (
          sameContext &&
          document.activeElement === textarea &&
          canShowGhost()
        ) {
          const reused = ctx.pendingSuggestion
          ctx.pendingSuggestion = null
          ctx.pendingForCaret = -1
          ctx.pendingForText = ''
          renderGhostAtCaret(reused)
          return
        }
        // テキストが変わってる → 古い pending は捨てて再リクエスト
        ctx.pendingSuggestion = null
        ctx.pendingForCaret = -1
        ctx.pendingForText = ''
      }

      requestGhost()
    }, IME_RESUME_DELAY_MS)
  }

  const onKeyDown = (e) => {
    // IME 変換中キーは無視（Safari: e.isComposing、他: keyCode 229）
    if (e.isComposing || e.keyCode === 229) return

    // Tab 採用 / Esc 破棄
    if (e.key === 'Tab' && ctx.ghostVisible) {
      e.preventDefault()
      e.stopPropagation()
      acceptGhost()
      return
    }
    if (e.key === 'Escape') {
      if (ctx.ghostVisible) {
        e.preventDefault()
        // 明示破棄のみ記録（blur / カーソル移動経由の hideGhost は「無視」扱いで記録しない）
        if (onEvent) onEvent('dismiss')
        hideGhost()
      }
      return
    }
    // カーソル移動系は「破棄して再トリガしない」
    if (
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'Home' ||
      e.key === 'End' ||
      e.key === 'PageUp' ||
      e.key === 'PageDown'
    ) {
      hideGhost()
    }
    // 上記以外のキー（文字入力・IME切替・修飾キー・F系・CapsLock など）は何もしない。
    // 実際のテキスト変更は input イベントで requestGhost が拾うので、ここで abort 不要。
    // 特に IME モード切替キー（半角/全角・CapsLock など）で abort されないようにするため。
  }

  const onInput = () => {
    // input イベントは IME 中にも発火する（composing === true のときは silent）
    if (ctx.composing) return
    requestGhost()
  }

  const onBlur = () => {
    hideGhost()
  }

  const onScroll = () => {
    if (ctx.ghostVisible) {
      // スクロール連動: ghost span を再配置
      syncGhostOverlayStyle(overlay, textarea)
      renderGhostAtCaret(ctx.ghostText)
    }
  }

  const onClick = () => {
    // クリックでカーソル位置変わる → ghost を消すだけ（再リクエストはしない）
    // クリック直後にユーザーが書きたいのは「自分の言葉」なので、AI 提案は出さない
    hideGhost()
  }

  ctx.handlers = {
    onCompositionStart,
    onCompositionEnd,
    onKeyDown,
    onInput,
    onBlur,
    onScroll,
    onClick
  }

  textarea.addEventListener('compositionstart', onCompositionStart)
  textarea.addEventListener('compositionend', onCompositionEnd)
  textarea.addEventListener('keydown', onKeyDown)
  textarea.addEventListener('input', onInput)
  textarea.addEventListener('blur', onBlur)
  textarea.addEventListener('scroll', onScroll)
  textarea.addEventListener('click', onClick)

  // textarea リサイズ追従
  if (typeof ResizeObserver !== 'undefined') {
    ctx.resizeObserver = new ResizeObserver(() => {
      if (ctx.ghostVisible) {
        syncGhostOverlayStyle(overlay, textarea)
        renderGhostAtCaret(ctx.ghostText)
      }
    })
    ctx.resizeObserver.observe(textarea)
  }

  state.set(textarea, ctx)

  function destroy() {
    clearTriggerTimer()
    clearResumeTimer()
    abortInflight()
    try {
      textarea.removeEventListener('compositionstart', onCompositionStart)
      textarea.removeEventListener('compositionend', onCompositionEnd)
      textarea.removeEventListener('keydown', onKeyDown)
      textarea.removeEventListener('input', onInput)
      textarea.removeEventListener('blur', onBlur)
      textarea.removeEventListener('scroll', onScroll)
      textarea.removeEventListener('click', onClick)
    } catch (e) {
      /* noop */
    }
    if (ctx.resizeObserver) {
      try {
        ctx.resizeObserver.disconnect()
      } catch (e) {
        /* noop */
      }
    }
    destroyGhostOverlay(overlay)
    state.delete(textarea)
  }

  return { destroy }
}
